import { useMutation, useQueryClient } from "@tanstack/react-query";

import { deleteCarImage } from "./delete-car-image";
import { getCarImagesQueryOptions } from "./get-car-images";
import { uploadCarImage } from "./upload-car-image";

import type { MutationConfig } from "@/lib/react-query";
import type { Attachment } from "@/types/attachment";

export type GalleryUpdatePayload = {
  carId: string;
  filesToAdd: File[];
  fileIdsToRemove: string[];
};

export const updateCarGallery = async ({
  carId,
  filesToAdd,
  fileIdsToRemove,
}: GalleryUpdatePayload) => {
  for (const fileId of fileIdsToRemove) {
    await deleteCarImage({ carId, fileId });
  }

  const uploaded: Attachment[] = [];
  for (const file of filesToAdd) {
    const result = await uploadCarImage({ carId, file });
    uploaded.push(result as Attachment);
  }

  return uploaded;
};

type UseUpdateCarGalleryOptions = {
  mutationConfig?: MutationConfig<typeof updateCarGallery>;
};

export const useUpdateCarGallery = ({ mutationConfig }: UseUpdateCarGalleryOptions = {}) => {
  const queryClient = useQueryClient();

  const { onSuccess, ...restConfig } = mutationConfig || {};

  return useMutation({
    onSuccess: (data, { carId, ...args }, ...rest) => {
      queryClient.invalidateQueries({
        queryKey: getCarImagesQueryOptions(carId).queryKey,
      });
      onSuccess?.(data, { carId, ...args }, ...rest);
    },
    ...restConfig,
    mutationFn: updateCarGallery,
  });
};
